import React, { useState } from "react";
import { BiCheck, BiCopy } from "react-icons/bi";
import { SiPython } from "react-icons/si";

const PythonSDK = () => {
    // List of all functions for index and IDs
    const functions = [
        { id: "installation", title: "Installation" },
        { id: "authentication", title: "Authentication" },
        { id: "get_all", title: "Get All" },
        { id: "find_one", title: "Find one" },
        { id: "find_many", title: "Find many" },
        { id: "insert_one", title: "Insert one" },
        { id: "insert_many", title: "Insert many" },
        { id: "update_one", title: "Update one" },
        { id: "update_many", title: "Update many" },
        { id: "replace_one", title: "Replace one" },
        { id: "delete_one", title: "Delete one" },
        { id: "delete_many", title: "Delete many" },
        { id: "count_records", title: "Count Records" },
        { id: "ping", title: "Ping" },
    ];

    // Scroll handler
    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
        }
    };
    return (
        <div className="doc_page_cont">
            <p className="doc_page_cont_title"><SiPython /> Database</p>

            <p className="doc_page_cont_paragraph">
                The <strong>Erioon Python SDK</strong> provides a streamlined way to interact with your Erioon database collections using Python.
                It handles authentication, database connections, and all CRUD operations through a simple and secure interface.
            </p>

            {/* Index Section */}
            <p className="doc_page_cont_subtitle">Index</p>
            <div className="doc_list" style={{ cursor: "pointer" }}>
                {functions.map(({ id, title }) => (
                    <p key={id} onClick={() => scrollToSection(id)}>
                        <i className="fa-regular fa-circle"></i> {title}
                    </p>
                ))}
            </div>

            <SDKFunction
                id="installation"
                title="Installation"
                description={
                    <>
                        <p>
                            Installing the Erioon Python SDK is quick and easy using the standard <code>pip</code> package manager.
                        </p>
                        <p>Run the following command in your terminal to install the SDK:</p>
                    </>
                }
                code={`pip install erioon`}
            />



            <SDKFunction
                id="authentication"
                title="Authentication"
                description={
                    <>
                        <p>
                            The Erioon Python SDK allows you to interact with your database
                            securely using two types of authentication:
                        </p>

                        <ul className="doc_list">
                            <li className="doc_page_cont_paragraph">
                                <strong>API Key:</strong> Grants full access to all your
                                resources (read/write/delete). Best used for trusted backend
                                systems or admin operations.
                            </li>
                            <li className="doc_page_cont_paragraph">
                                <strong>Access Credentials:</strong> Provide more granular
                                control. You can assign <em>read/write</em> or{" "}
                                <em>read-only</em> permissions to users. Ideal for client-side
                                apps or restricted environments.
                            </li>
                        </ul>

                        <p>Below is how you authenticate using the SDK with either method:</p>
                    </>
                }
                code={`from erioon.login import ErioonLogin

# Option 1: API Key Authentication (Full access)
api_key = "your-api-key"
client = ErioonLogin(api_key)

# Option 2: Access Credentials Authentication (Role-based access)
username = "user@example.com"
password = "user-password"
client = ErioonLogin(f"{username}:{password}")

# Once authenticated, select your database and collection
db = client["your-db-id"]
col = db["your-collection-id"]`}
            />

            {/* Function Documentation */}
            <SDKFunction
                id="get_all"
                title="Get All"
                description={
                    <>
                        <p>
                            The <code>get_all</code> method retrieves up to a specified number of documents from a collection.
                            It's useful for loading large batches of data without needing complex filters.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Scans all the data located under the collection path (<code>db_id/coll_id/</code>).</li>
                            <li>Parses shard files, each containing batches of records.</li>
                            <li>Stops reading as soon as the requested record limit is reached (up to <strong>1,000,000</strong>).</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>limit</code> (int): Maximum number of documents to retrieve. Defaults to 1000.</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple: <code>(status: dict, status_code: int)</code></li>
                            <li><code>status["results"]</code> contains the list of documents (if successful).</li>
                            <li>HTTP status codes:
                                <ul>
                                    <li><code>200</code> – Success.</li>
                                    <li><code>404</code> – No records found or collection missing.</li>
                                    <li><code>500</code> – Internal error during processing.</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`# Fetch up to 1000 records from the collection
all_col, status = col.get_all(limit=1000)

if status == 200:
    print("Records retrieved:", len(all_col["results"]))
else:
    print("Error:", all_col.get("error", "Unknown failure"))`}
            />


            <SDKFunction
                id="find_one"
                title="Find One"
                description={
                    <>
                        <p>
                            The <code>find_one</code> method searches for a <strong>single record</strong> that matches all provided filters within a collection.
                            This method is ideal when you're looking for a unique or specific match.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Scans each sharded collection located at <code>{`db_id/coll_id/`}</code>.</li>
                            <li>Iterates over each record in the collection and applies your filters to check for a match.</li>
                            <li>Supports nested field queries using <strong>dot notation</strong> (e.g., <code>"address.city"</code>).</li>
                            <li>Returns the <strong>first</strong> matching record encountered.</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li>
                                <code>filters</code> (dict): Dictionary of fields and values to match. All must be satisfied.
                                Supports nested fields via dot notation.
                            </li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple: <code>(status: dict, status_code: int)</code></li>
                            <li><code>status["record"]</code> contains the document if found.</li>
                            <li>HTTP status codes:
                                <ul>
                                    <li><code>200</code> – Match found and returned.</li>
                                    <li><code>404</code> – No match found or collection doesn't exist.</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`# Find a single product by its unique ID
product, status = col.find_one(filters={"_id": "some-id"})

if status == 200:
    print("Found:", product["record"])
else:
    print("Not found or error:", product.get("error", "Unknown"))`}
            />


            <SDKFunction
                id="find_many"
                title="Find Many"
                description={
                    <>
                        <p>
                            The <code>find_many</code> method allows you to retrieve <strong>multiple documents</strong> that match the given filters from a collection.
                            It is useful for performing searches with conditions, pagination, or analytics on subsets of data.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Scans all collections within the path <code>{`db_id/coll_id/`}</code>.</li>
                            <li>Filters records by matching all specified criteria using <strong>AND logic</strong>.</li>
                            <li>Supports nested fields via <strong>dot notation</strong> (e.g., <code>"profile.country"</code>).</li>
                            <li>Stops once the specified <code>limit</code> of records is reached (defaults to 1000 if not set).</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li>
                                <code>filters</code> (dict): Field-value pairs to match. All must be satisfied. Nested fields are supported.
                            </li>
                            <li>
                                <code>limit</code> (int): Maximum number of records to return (must not exceed 1,000,000).
                            </li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple: <code>(status: dict, status_code: int)</code></li>
                            <li><code>status["results"]</code> is a list of matching records (if any).</li>
                            <li><code>status["count"]</code> indicates how many were returned.</li>
                            <li>HTTP status codes:
                                <ul>
                                    <li><code>200</code> – Matching records were found and returned.</li>
                                    <li><code>404</code> – No records matched or the collection doesn't exist.</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`# Find up to 50 users with role "admin"
admins, status = col.find_many(filters={"role": "admin"}, limit=50)

if status == 200:
    print("Found", admins["count"], "admins")
else:
    print("No matches or error:", admins.get("error", "Unknown"))`}
            />


            <SDKFunction
                id="insert_one"
                title="Insert One"
                description={
                    <>
                        <p>
                            Insert a single record into the specified collection with robust handling and indexing.
                        </p>

                        <ul className="doc_list">
                            <li>
                                If the incoming record lacks an <code>_id</code> field, the system automatically generates a new UUID to uniquely identify the record.
                            </li>
                            <li>
                                If the provided <code>_id</code> already exists (i.e., a duplicate), the system generates a new unique <code>_id</code> to prevent collisions and updates the record accordingly.
                            </li>
                            <li>
                                The record is created or appended in the appropriate shard file to ensure data distribution and scalability.
                            </li>
                            <li>
                                The <code>index</code> file is updated to maintain an accurate mapping between the record’s <code>_id</code> and its shard location, enabling efficient queries.
                            </li>
                            <li>
                                Success or error states are logged asynchronously to avoid blocking and provide auditability.
                            </li>
                        </ul>

                        <p><strong>Arguments:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id_cont</code>: Identifier for the user performing the insertion (for multi-tenant and permissions handling).</li>
                            <li><code>database</code>: Target database name.</li>
                            <li><code>collection</code>: Target collection name.</li>
                            <li><code>record</code>: Dictionary representing the record to insert; may or may not include an <code>_id</code>.</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(response dict, status code)</code> where:</li>
                            <ul>
                                <li><code>response dict</code> contains insertion results or error details.</li>
                                <li><code>status code</code> is an HTTP-like status indicating success or failure.</li>
                            </ul>
                        </ul>
                    </>
                }
                code={`new_doc = {"name": "Mouse", "price": 29.99}
res, status = col.insert_one(new_doc)
print(status)`}
            />


            <SDKFunction
                id="insert_many"
                title="Insert Many"
                description={
                    <>
                        <p>Insert multiple records in bulk efficiently and reliably.</p>

                        <ul className="doc_list">
                            <li><code>data</code> is a list of dictionaries, each representing a record to insert.</li>
                            <li>For each record:
                                <ul>
                                    <li>Ensure it has a unique <code>_id</code> (generate a new UUID if missing or duplicate).</li>
                                    <li>Write the record to the appropriate shard file.</li>
                                    <li>Update <code>index</code> with the <code>_id</code> to shard mapping.</li>
                                </ul>
                            </li>
                            <li>Return an aggregate success or failure response summarizing the operation.</li>
                        </ul>

                        <p><strong>Arguments:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id_cont</code>: User identifier for authentication and permissions.</li>
                            <li><code>database</code>: Target database name.</li>
                            <li><code>collection</code>: Target collection name.</li>
                            <li><code>data</code>: List of record dictionaries to insert.</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(response dict, status code)</code> where:</li>
                            <ul>
                                <li><code>response dict</code> summarizes the insert operation results or errors.</li>
                                <li><code>status code</code> is an HTTP-like code indicating overall success or failure.</li>
                            </ul>
                        </ul>
                    </>
                }
                code={`docs = [{"name": "Cable"}, {"name": "Stand"}]
res, status = col.insert_many(docs)
print(status)`}
            />


            <SDKFunction
                id="update_one"
                title="Update One"
                description={
                    <>
                        <p>
                            Update a single document that matches the given filter with supported update operations.
                            This is commonly used with <code>$set</code> but supports other operators as well.
                        </p>

                        <p><strong>Supported update operations in <code>update_query</code>:</strong></p>
                        <ul className="doc_list">
                            <li><code>$set</code>: Overwrites the value at the specified (possibly nested) key.</li>
                            <li><code>$push</code>: Appends a value to a list at the specified key, or initializes the list if it doesn't exist.</li>
                            <li><code>$remove</code>: Deletes the specified key from the record.</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): Identifier of the user making the update request.</li>
                            <li><code>db_id</code> (str): Database identifier (used as directory prefix).</li>
                            <li><code>coll_id</code> (str): Collection identifier (used as a subdirectory under the database).</li>
                            <li><code>filter_query</code> (dict): Key-value pairs that must match exactly for the record to be updated.</li>
                            <li><code>update_query</code> (dict): Update operations to apply using the supported operators.</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(dict, int)</code> where:</li>
                            <ul>
                                <li>The dict contains either:
                                    <ul>
                                        <li><code>"success"</code>: Confirmation message if the update succeeded.</li>
                                        <li><code>"error"</code>: Error message if the update failed.</li>
                                    </ul>
                                </li>
                                <li>HTTP status code:
                                    <ul>
                                        <li><code>200</code>: Record updated successfully.</li>
                                        <li><code>404</code>: No matching record or collection found.</li>
                                    </ul>
                                </li>
                            </ul>
                        </ul>

                        <p>Internal exceptions are caught silently, so no explicit 500 errors are returned.</p>
                    </>
                }
                code={`res, status = col.update_one(
    {"name": "Mouse"},
    {"$set": {"price": 24.99}}
)
print(status)`}
            />

            <SDKFunction
                id="update_many"
                title="Update Many"
                description={
                    <>
                        <p>
                            Perform multiple record updates based on a list of filter-and-update tasks.
                            Each task specifies a filter to match records and an update query to apply using supported operators.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Applies updates across the specified collection.</li>
                            <li>Supports update operations: <code>$set</code>, <code>$push</code>, and <code>$remove</code>.</li>
                            <li>Tracks the number of records updated per task.</li>
                            <li>Logs successes asynchronously for observability.</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): ID of the user performing the updates.</li>
                            <li><code>db_id</code> (str): Database identifier.</li>
                            <li><code>coll_id</code> (str): Collection identifier.</li>
                            <li><code>update_tasks</code> (list of dict): List of update tasks, each containing:
                                <ul>
                                    <li><code>filter</code> (dict): Key-value pairs to match records.</li>
                                    <li><code>update</code> (dict): Update operations to apply.</li>
                                </ul>
                            </li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(dict, int)</code> where:</li>
                            <ul>
                                <li>The dict summarizes:
                                    <ul>
                                        <li>Total number of records updated across all tasks.</li>
                                        <li>Detailed results for each task, including counts or errors.</li>
                                    </ul>
                                </li>
                                <li>HTTP status code:
                                    <ul>
                                        <li><code>200</code>: One or more records were updated.</li>
                                        <li><code>404</code>: No records matched or were updated.</li>
                                    </ul>
                                </li>
                            </ul>
                        </ul>
                    </>
                }
                code={`updates = [
  {"filter": {"category": "electronics"}, "update": {"$push": {"tags": "sale"}}},
  {"filter": {"sku": "LS789"}, "update": {"$set": {"stock": 120}}}
]
res, status = col.update_many(updates)
print(status)`}
            />


            <SDKFunction
                id="replace_one"
                title="Replace One"
                description={
                    <>
                        <p>
                            Replace a single document that matches the filter with a completely new record.
                            This operation searches the collection under <code>{`{db_id}/{coll_id}/`}</code> for a record matching a single-field filter.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Searches collection for a record matching exactly one filter key-value pair.</li>
                            <li>Replaces the entire matched record with the provided <code>replacement</code> dictionary.</li>
                            <li>If <code>replacement</code> does not include <code>_id</code>, preserves the original record’s <code>_id</code>.</li>
                            <li>Only a single field is allowed in the filter to uniquely identify the record.</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): User performing the replacement.</li>
                            <li><code>db_id</code> (str): Database identifier.</li>
                            <li><code>coll_id</code> (str): Collection identifier.</li>
                            <li><code>filter_query</code> (dict): Dictionary with exactly one key-value pair to match the target record.</li>
                            <li><code>replacement</code> (dict): New record to replace the existing one.</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(dict, int)</code> containing:
                                <ul>
                                    <li>Response dictionary with either:
                                        <ul>
                                            <li><code>"success"</code> message if replacement succeeded.</li>
                                            <li><code>"error"</code> message if no matching record was found or filter was invalid.</li>
                                        </ul>
                                    </li>
                                    <li>HTTP status codes:
                                        <ul>
                                            <li><code>200</code>: Replacement succeeded.</li>
                                            <li><code>400</code>: Invalid filter (must be exactly one field).</li>
                                            <li><code>404</code>: No matching record found.</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`replacement = {
  "name": "Ergonomic Mouse",
  "sku": "WM123",
  "price": 27.99
}
res, status = col.replace_one({"sku": "WM123"}, replacement)
print(status)`}
            />


            <SDKFunction
                id="delete_one"
                title="Delete One"
                description={
                    <>
                        <p>
                            Delete a single document from the collection by specifying its unique identifier or a matching filter.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>The record to delete can be identified by the unique <code>_id</code> field or by any nested key-value pair.</li>
                            <li>The method searches the collection for a matching record and removes it if found.</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): Identifier of the user performing the deletion.</li>
                            <li><code>db_id</code> (str): Database identifier containing the collection.</li>
                            <li><code>coll_id</code> (str): Collection identifier.</li>
                            <li><code>data_to_delete</code> (dict): Dictionary with either <code>_id</code> or key-value pair to find the record to delete.</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(dict, int)</code> where:
                                <ul>
                                    <li>Dictionary contains success or error message.</li>
                                    <li>Status code indicates operation result (e.g., 200 for success, 404 if not found).</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`res, status = col.delete_one({"_id": "prod_1000"})
print(status)`}
            />


            <SDKFunction
                id="delete_many"
                title="Delete Many"
                description={
                    <>
                        <p>
                            Delete multiple documents from the collection by specifying a list of filter objects.
                        </p>

                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Supports deletion by unique <code>_id</code> or arbitrary key-value pair filters.</li>
                            <li>Processes deletions in batches to improve performance and isolate errors.</li>
                        </ul>

                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): Identifier of the user making the deletion request.</li>
                            <li><code>db_id</code> (str): Database identifier.</li>
                            <li><code>coll_id</code> (str): Collection identifier.</li>
                            <li><code>data_to_delete_list</code> (list of dict): List of filters specifying records to delete; each dict must have either <code>_id</code> or a key-value pair.</li>
                            <li><code>batch_size</code> (int): Number of deletions to process per batch (optional).</li>
                        </ul>

                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple <code>(dict, int)</code> where:
                                <ul>
                                    <li>The dict contains a summary of successful deletions and any failures.</li>
                                    <li>The status code indicates overall success (200) or failure.</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`to_delete = [{"name": "Old"}, {"sku": "LS789"}]
res, status = col.delete_many(to_delete)
print(status)`}
            />


            <SDKFunction
                id="count_records"

                title="Count Records"
                description={
                    <>
                        <p>
                            Return the total number of documents in the collection by counting all records
                            across all shards within the specified collection directory.
                        </p>
                        <p><strong>Behavior:</strong></p>
                        <ul className="doc_list">
                            <li>Aggregates the count of records in every collection under <code>{'{db_id}/{coll_id}/'}</code>.</li>
                        </ul>
                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): ID of the user making the request.</li>
                            <li><code>db_id</code> (str): Identifier of the database.</li>
                            <li><code>coll_id</code> (str): Identifier of the collection.</li>
                        </ul>
                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>A tuple (<code>int</code>, <code>int</code>) containing:
                                <ul>
                                    <li>Total count of records (int).</li>
                                    <li>HTTP status code (200 if successful, 404 if collection not found).</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`count, status = col.count_records()
print(count)
print(status)`}
            />


            <SDKFunction
                id="ping"
                title="Ping"
                description={
                    <>
                        <p>
                            Check if a specific collection exists within a database.
                        </p>
                        <p><strong>Parameters:</strong></p>
                        <ul className="doc_list">
                            <li><code>user_id</code> (str): ID of the user making the request.</li>
                            <li><code>db_id</code> (str): Database identifier (folder prefix).</li>
                            <li><code>coll_id</code> (str): Collection identifier (folder prefix).</li>
                        </ul>
                        <p><strong>Returns:</strong></p>
                        <ul className="doc_list">
                            <li>
                                Tuple (<code>dict</code>, <code>int</code>) with:
                                <ul>
                                    <li>Status dictionary with keys "status": "OK" or "KO".</li>
                                    <li>HTTP status code: 200 if collection is found, 404 if missing, 500 on exception.</li>
                                </ul>
                            </li>
                        </ul>
                    </>
                }
                code={`res, status = col.ping()
print(status)`}
            />


            <p className="doc_page_cont_paragraph">
                We're continuously updating and improving Erioon, and we would love to receive your feedback. For more information or assistance, please contact us at support@erioon.com or visit our help center at www.erioon.com/help-center.
            </p>
        </div>
    );
};


const SDKFunction = ({ id, title, description, code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);

            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy!", err);
        }
    };

    return (
        <div className="sdk_section" id={id}>
            <p className="doc_page_cont_subtitle">{title}</p>
            <div className="doc_page_cont_paragraph">{description}</div>
            <div className="code_block_docs">
                <button onClick={handleCopy}>
                    {copied ? <BiCheck /> : <BiCopy />}
                </button>
                <pre>{code}</pre>
            </div>
        </div>
    );
};

export default PythonSDK;

